/// Port of mobile/lib/game/ui/game_screen.dart.
///
/// Fixed 600x150 canvas, CSS-scaled to a 4:1 aspect. Pointer halves and
/// keyboard both drive the same DinoDash methods the Flutter Listener /
/// KeyboardEvents handlers call.

import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { appRoutes } from '@/app/router/routes';
import { appContentMaxWidth } from '@/app/ui/appContentWidth';
import { useAuthStore } from '@/auth/handler/authStore';
import { keysEqual } from '@/settings/data/keyBindings';
import { useIsDarkMode, useSettingsStore, useThemeColors } from '@/settings/handler/settingsStore';
import { FlashText, textShadow } from '@/ui/FlashText';
import { PixelContainer } from '@/ui/pixel/PixelContainer';
import { PixelIcon } from '@/ui/pixel/PixelIcon';
import { worldHeight, worldWidth } from '../world';
import { useGameSessionStore } from '@/game/handler/gameSessionStore';
import { GameHud } from './GameHud';
import { formatScore } from '@/utils/formatScore';
import { useDinoDash } from './useDinoDash';

const chromeGuardPadding = 8;

export function GameScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const colors = useThemeColors();
  const isDark = useIsDarkMode();
  const musicEnabled = useSettingsStore((s) => s.musicEnabled);
  const sfxEnabled = useSettingsStore((s) => s.sfxEnabled);
  const musicVolume = useSettingsStore((s) => s.musicVolume);
  const sfxVolume = useSettingsStore((s) => s.sfxVolume);
  const controlsSwapped = useSettingsStore((s) => s.controlsSwapped);
  const jumpKey = useSettingsStore((s) => s.jumpKey);
  const duckKey = useSettingsStore((s) => s.duckKey);
  const user = useAuthStore((s) => s.user);

  const { canvasRef, game, snapshot, audio } = useDinoDash({
    isDarkMode: isDark,
    highScore: user.highScore,
    musicEnabled,
    sfxEnabled,
    musicVolume,
    sfxVolume,
    onGameOver: (payload) => {
      const { user, submitGameRun, updateHighScore } = useAuthStore.getState();
      if (user.isAnonymous) {
        void updateHighScore(payload.score);
        return;
      }
      void submitGameRun({
        score: payload.score,
        durationMs: payload.durationMs,
        jumpCount: payload.jumpCount,
        duckCount: payload.duckCount,
        deathCause: payload.deathCause,
        wasPersonalBest: payload.wasPersonalBest,
        platform: 'web',
      });
    },
  });

  const duckPointerRef = useRef<number | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef(audio);
  audioRef.current = audio;
  const bindingsRef = useRef({ jump: jumpKey, duck: duckKey });
  bindingsRef.current = { jump: jumpKey, duck: duckKey };

  // Keyboard uses the desktop bindings. Touch still uses screen halves.
  useEffect(() => {
    if (!game) return;

    const onKeyDown = (event: KeyboardEvent): void => {
      audioRef.current?.unlock();
      const bindings = bindingsRef.current;
      if (event.repeat && (keysEqual(event.key, bindings.jump) || keysEqual(event.key, bindings.duck))) {
        event.preventDefault();
        return;
      }
      if (game.handleKeyDown(event.key, bindings)) event.preventDefault();
    };
    const onKeyUp = (event: KeyboardEvent): void => {
      if (game.handleKeyUp(event.key, bindingsRef.current)) event.preventDefault();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [game]);

  // Tab visibility stands in for Flutter's AppLifecycleState.
  useEffect(() => {
    if (!game) return;

    const onVisibility = (): void => {
      if (document.hidden) {
        if (game.gameState.status === 'playing') game.pauseGame();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [game]);

  const endDuck = useCallback(
    (pointerId: number) => {
      if (duckPointerRef.current !== pointerId) return;
      duckPointerRef.current = null;
      if (game?.gameState.status === 'playing') game.setDuck(false);
    },
    [game],
  );

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!game) return;
      audioRef.current?.unlock();
      const stage = stageRef.current;
      if (!stage) return;

      const rect = stage.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const status = game.gameState.status;

      switch (status) {
        case 'waiting':
          // Start on finger lift — see onPointerUp.
          break;
        case 'gameOver':
          game.retryGame();
          break;
        case 'paused':
          game.resumeGame();
          break;
        case 'playing': {
          const isLeft = x < rect.width / 2;
          const isDuckSide = controlsSwapped ? !isLeft : isLeft;
          if (isDuckSide) {
            duckPointerRef.current = event.pointerId;
            stage.setPointerCapture(event.pointerId);
            game.setDuck(true);
          } else {
            game.requestJump();
          }
          break;
        }
      }
    },
    [game, controlsSwapped],
  );

  const onPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (game?.gameState.status === 'waiting') {
        game.startGame();
        return;
      }
      endDuck(event.pointerId);
    },
    [game, endDuck],
  );

  const { status, score, runTargetHighScore, newHighScoreThisRun } = snapshot;
  const isWaiting = status === 'waiting';
  const isGameOver = status === 'gameOver';
  const isPlaying = status === 'playing' || status === 'paused';
  const isNewHighScore = isGameOver && newHighScoreThisRun;

  useEffect(() => {
    useGameSessionStore.getState().setActive(isPlaying);
    return () => useGameSessionStore.getState().setActive(false);
  }, [isPlaying]);

  return (
    <div
      className="relative h-full min-h-full overflow-hidden"
      style={{ backgroundColor: colors.surface }}
    >
      <div className="absolute inset-0 flex items-center justify-center p-2">
        {/*
          Fit the 4:1 world into both landscape and portrait: width-capped by
          the viewport (and app content max), height-capped so tall phones
          still show the full run.
        */}
        <div
          className="flex w-full items-center justify-center"
          style={{ maxWidth: appContentMaxWidth }}
        >
          <canvas
            ref={canvasRef}
            width={worldWidth}
            height={worldHeight}
            className="pixelated block"
            style={{
              aspectRatio: `${worldWidth} / ${worldHeight}`,
              width: `min(100%, calc((100dvh - 1rem) * ${worldWidth / worldHeight}))`,
              maxWidth: '100%',
              maxHeight: 'calc(100dvh - 1rem)',
              height: 'auto',
            }}
          />
        </div>
      </div>

      {/* Full-screen input layer so taps anywhere start / jump / duck. */}
      <div
        ref={stageRef}
        className="absolute inset-0 touch-none-select-none"
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />

      {isWaiting && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <FlashText>
            <span
              className="leading-none"
              style={{
                fontSize: 28,
                color: colors.onSurface,
                textShadow: textShadow(colors.surface, 1.5),
              }}
            >
              {t('tapToPlay')}
            </span>
          </FlashText>
        </div>
      )}

      {isWaiting && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center p-2">
          <ChromeTapGuard>
            <div className="w-full" style={{ maxWidth: appContentMaxWidth }}>
              <HomeChromeBar
                profileLabel={user.isAnonymous ? t('authNotSignedIn') : user.username}
                onProfile={() => navigate(user.isAnonymous ? appRoutes.auth : appRoutes.user)}
                onRank={() => navigate(appRoutes.rank)}
                onStats={() => navigate(appRoutes.stats)}
                onSettings={() => navigate(appRoutes.settings)}
              />
              <LegalFooter />
            </div>
          </ChromeTapGuard>
        </div>
      )}

      {isGameOver && (
        <>
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <FlashText>
              <div className="flex flex-col items-center gap-2">
                {isNewHighScore ? (
                  <>
                    <div className="flex items-center gap-2">
                      <PixelIcon name="trophy" set="pixel_icons" size={26} color={colors.highScoreGold} />
                      <span
                        className="leading-none tracking-wide"
                        style={{
                          fontSize: 30,
                          color: colors.highScoreGold,
                          textShadow: textShadow(colors.surface, 1.5),
                        }}
                      >
                        {t('newHighScore')}
                      </span>
                    </div>
                    <span
                      className="leading-none"
                      style={{
                        fontSize: 34,
                        color: colors.highScoreGold,
                        textShadow: textShadow(colors.surface, 1.5),
                      }}
                    >
                      {formatScore(score)}
                    </span>
                  </>
                ) : (
                  <>
                    <span
                      className="leading-none tracking-widest"
                      style={{
                        fontSize: 32,
                        color: colors.onSurface,
                        textShadow: textShadow(colors.surface, 1.5),
                      }}
                    >
                      {t('gameOver')}
                    </span>
                    <span
                      className="leading-none"
                      style={{
                        fontSize: 34,
                        color: colors.onSurface,
                        textShadow: textShadow(colors.surface, 1.5),
                      }}
                    >
                      {formatScore(score)}
                    </span>
                  </>
                )}
                <span
                  className="leading-none tracking-wide"
                  style={{
                    fontSize: 26,
                    color: colors.onSurface,
                    textShadow: textShadow(colors.surface, 1.5),
                  }}
                >
                  {t('tapToRetry')}
                </span>
              </div>
            </FlashText>
          </div>

          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center p-2">
            <ChromeTapGuard>
              <div
                className="pointer-events-auto flex h-12 w-full items-center justify-center"
                style={{ maxWidth: appContentMaxWidth }}
              >
                <PixelContainer
                  radius={9}
                  pixelScale={2}
                  padding="8px 14px"
                  backgroundColor={colors.surfaceContainerHigh}
                  borderColor={colors.onSurface}
                  onClick={() => game?.goToHome()}
                >
                  <div className="flex items-center gap-2">
                    <PixelIcon name="home" set="pixel_icons" size={22} color={colors.onSurface} />
                    <span className="leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
                      {t('home')}
                    </span>
                  </div>
                </PixelContainer>
              </div>
            </ChromeTapGuard>
          </div>
        </>
      )}

      {isPlaying && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center pt-2">
          <div className="w-full" style={{ maxWidth: appContentMaxWidth }}>
            <GameHud
              score={score}
              highScore={runTargetHighScore}
              celebratingHighScore={newHighScoreThisRun}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ChromeTapGuard({ children }: { children: ReactNode }) {
  return (
    <div
      className="pointer-events-auto inline-flex max-w-full"
      style={{ padding: chromeGuardPadding }}
      onPointerDown={(event) => event.stopPropagation()}
      onPointerUp={(event) => event.stopPropagation()}
      onPointerCancel={(event) => event.stopPropagation()}
    >
      {children}
    </div>
  );
}

function HomeChromeBar({
  profileLabel,
  onProfile,
  onRank,
  onStats,
  onSettings,
}: {
  profileLabel: string;
  onProfile: () => void;
  onRank: () => void;
  onStats: () => void;
  onSettings: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-2">
        <ChromeIconButton name="trophy" onClick={onRank} />
        <ChromeIconButton name="chart" onClick={onStats} />
        <ChromeIconButton name="settings" onClick={onSettings} />
      </div>
      <ProfileChip label={profileLabel} onClick={onProfile} />
    </div>
  );
}

function ChromeIconButton({
  name,
  onClick,
}: {
  name: string;
  onClick: () => void;
}) {
  const colors = useThemeColors();
  return (
    <PixelContainer
      radius={9}
      pixelScale={2}
      padding="8px 20px"
      backgroundColor={colors.surfaceContainerHighest}
      borderColor={colors.onSurface}
      onClick={onClick}
    >
      <PixelIcon name={name} set="pixel_icons" size={22} color={colors.onSurface} />
    </PixelContainer>
  );
}

function ProfileChip({ label, onClick }: { label: string; onClick: () => void }) {
  const colors = useThemeColors();
  return (
    <PixelContainer
      radius={9}
      pixelScale={2}
      padding="8px 20px"
      backgroundColor={colors.surfaceContainerHighest}
      borderColor={colors.onSurface}
      onClick={onClick}
    >
      <div className="flex max-w-[140px] items-center gap-2">
        <PixelIcon name="user" set="pixel_icons" size={22} color={colors.onSurface} />
        <span
          className="truncate leading-none"
          style={{ fontSize: 18, color: colors.onSurface }}
        >
          {label}
        </span>
      </div>
    </PixelContainer>
  );
}

function LegalFooter() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const linkStyle = {
    fontSize: 16,
    color: colors.onSurface,
    opacity: 0.72,
    textDecoration: 'underline',
    textUnderlineOffset: 3,
  } as const;

  return (
    <div className="pointer-events-auto mt-6 flex justify-center gap-4 pb-1">
      <a href="/privacy-policy/" style={linkStyle}>
        {t('legalPrivacy')}
      </a>
      <a href="/terms-of-service/" style={linkStyle}>
        {t('legalTerms')}
      </a>
    </div>
  );
}
